import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getInvoice, updateInvoice, sendInvoice, getDeliverables, uploadDeliverable, assignDeliverableInvoice } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/ui/page-header';
import { SectionRule } from '@/components/ui/section-rule';
import { EmptyState } from '@/components/ui/empty-state';
import { StatusDot } from '@/components/ui/status-dot';
import { Field } from '@/components/ui/field';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { ArrowLeft, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (a) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(a || 0);

export default function EditInvoice() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [taxPercent, setTaxPercent] = useState(0);
  const [dueDate, setDueDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [deliverables, setDeliverables] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Only files this invoice could plausibly unlock: unassigned ones (which
  // unlock on any project payment) and ones already gated here. Files gated
  // to a DIFFERENT invoice are out of scope for this page — that's what the
  // project workspace's full milestone table is for.
  const fetchDeliverables = (projectId) => {
    if (!projectId) return;
    getDeliverables(projectId)
      .then(r => setDeliverables(r.data.filter(d => !d.invoice_id || d.invoice_id === id)))
      .catch(() => {});
  };

  useEffect(() => {
    getInvoice(id).then(res => {
      const inv = res.data;
      setInvoice(inv);
      setLineItems(inv.line_items || [{ description: '', qty: 1, rate: 0, amount: 0 }]);
      setTaxPercent(inv.tax_percent || 0);
      setDueDate(inv.due_date ? inv.due_date.slice(0, 10) : '');
      setLoading(false);
      fetchDeliverables(inv.project_id);
    }).catch(() => { navigate('/invoices'); });
  }, [id, navigate]);

  // New uploads default to gating on THIS invoice — the whole point of
  // uploading from here is usually "this is the deposit/milestone this
  // invoice covers." Reassigning to "any payment" stays one click away in
  // the project workspace.
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await uploadDeliverable(invoice.project_id, file, id);
      toast.success(`${file.name} uploaded`);
      fetchDeliverables(invoice.project_id);
    } catch {
      toast.error('Upload failed');
    }
    setUploading(false);
    e.target.value = '';
  };

  const handleUnassign = async (deliverableId) => {
    try {
      await assignDeliverableInvoice(invoice.project_id, deliverableId, null);
      fetchDeliverables(invoice.project_id);
    } catch { toast.error('Failed to update'); }
  };

  // Keeps whatever the user typed (including '') so backspacing a 0 or 1
  // actually clears the box instead of snapping straight back — only the
  // derived `amount` needs a real number, not the qty/rate fields themselves.
  const updateLineItem = (idx, field, value) => {
    setLineItems(prev => {
      const items = [...prev];
      items[idx] = { ...items[idx], [field]: value };
      if (field === 'qty' || field === 'rate') {
        items[idx].amount = (parseFloat(items[idx].qty) || 0) * (parseFloat(items[idx].rate) || 0);
      }
      return items;
    });
  };

  // Normalizes a blank qty/rate back to 0 once the user leaves the field,
  // so a saved invoice never carries an empty-string quantity or rate.
  const blurLineItem = (idx, field) => {
    setLineItems(prev => {
      const items = [...prev];
      if (items[idx][field] === '') {
        items[idx] = { ...items[idx], [field]: 0 };
      }
      return items;
    });
  };

  const addLineItem = () => setLineItems(prev => [...prev, { description: '', qty: 1, rate: 0, amount: 0 }]);
  const removeLineItem = (idx) => setLineItems(prev => prev.filter((_, i) => i !== idx));

  const subtotal = lineItems.reduce((s, item) => s + (item.amount || 0), 0);
  const taxAmount = subtotal * ((parseFloat(taxPercent) || 0) / 100);
  const total = subtotal + taxAmount;

  const handleSave = async () => {
    if (lineItems.some(i => !i.description?.trim())) { toast.error('All line items need a description'); return; }
    setSaving(true);
    try {
      const res = await updateInvoice(id, {
        line_items: lineItems.map(item => ({
          ...item,
          qty: parseFloat(item.qty) || 0,
          rate: parseFloat(item.rate) || 0,
        })),
        tax_percent: parseFloat(taxPercent) || 0,
        due_date: dueDate || null,
      });
      setInvoice(res.data);
      toast.success('Invoice updated');
    } catch { toast.error('Failed to update invoice'); }
    setSaving(false);
  };

  const handleSend = async () => {
    try {
      const res = await sendInvoice(id);
      setInvoice(res.data);
      toast.success(invoice.status === 'draft' ? 'Invoice sent!' : 'Invoice resent!');
    } catch { toast.error('Failed to send invoice'); }
  };

  if (loading) return <div className="h-64 animate-pulse rounded-md bg-wash" />;
  if (!invoice) return null;

  const isPaid = invoice.status === 'paid';

  const backLink = (
    <button
      onClick={() => navigate('/invoices')}
      className="mb-4 inline-flex items-center gap-1.5 text-small text-ink-muted transition-colors hover:text-forest"
    >
      <ArrowLeft strokeWidth={1.5} className="h-3.5 w-3.5" /> Invoices
    </button>
  );

  return (
    <div className="mx-auto max-w-[760px]" data-testid="edit-invoice-page">
      {backLink}

      <PageHeader
        title={isPaid ? `Invoice #${invoice.invoice_number}` : `Edit invoice #${invoice.invoice_number}`}
        description={isPaid ? 'This invoice has been paid and is read-only.' : undefined}
        actions={!isPaid && (
          <Button variant="outline" onClick={handleSend}>
            {invoice.status === 'draft' ? 'Send invoice' : 'Resend invoice'}
          </Button>
        )}
      />

      <section>
        <SectionRule label="Client" />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Client" htmlFor="edit-invoice-client">
            <Input id="edit-invoice-client" value={invoice.client?.name || 'Unknown'} disabled />
          </Field>
          <Field label="Invoice number" htmlFor="edit-invoice-number">
            <Input id="edit-invoice-number" value={`#${invoice.invoice_number}`} disabled className="tabular-nums" />
          </Field>
        </div>
        {invoice.client?.address && (
          <div className="mt-4">
            <p className="text-micro uppercase text-ink-subtle">Client address</p>
            <p className="mt-1 whitespace-pre-line text-body text-ink-muted">{invoice.client.address}</p>
          </div>
        )}
      </section>

      <section className="mt-10">
        <SectionRule
          label="Line items"
          action={!isPaid && <Button variant="outline" size="sm" onClick={addLineItem}>Add row</Button>}
        />
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Description</TableHead>
              <TableHead className="w-[90px] text-right">Qty</TableHead>
              <TableHead className="w-[110px] text-right">Rate</TableHead>
              <TableHead className="w-[110px] text-right">Amount</TableHead>
              {!isPaid && <TableHead className="w-10"><span className="sr-only">Remove</span></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {lineItems.map((item, i) => (
              <TableRow key={i} className="hover:bg-transparent">
                <TableCell className="px-1">
                  <Input
                    value={item.description}
                    onChange={e => updateLineItem(i, 'description', e.target.value)}
                    placeholder="Description"
                    aria-label={`Line item ${i + 1} description`}
                    disabled={isPaid}
                  />
                </TableCell>
                <TableCell className="px-1">
                  <Input
                    type="number"
                    value={item.qty}
                    onChange={e => updateLineItem(i, 'qty', e.target.value)}
                    onBlur={() => blurLineItem(i, 'qty')}
                    className="text-right tabular-nums"
                    aria-label={`Line item ${i + 1} quantity`}
                    disabled={isPaid}
                  />
                </TableCell>
                <TableCell className="px-1">
                  <Input
                    type="number"
                    value={item.rate}
                    onChange={e => updateLineItem(i, 'rate', e.target.value)}
                    onBlur={() => blurLineItem(i, 'rate')}
                    className="text-right tabular-nums"
                    aria-label={`Line item ${i + 1} rate`}
                    disabled={isPaid}
                  />
                </TableCell>
                <TableCell className="text-right text-ink">{formatCurrency(item.amount)}</TableCell>
                {!isPaid && (
                  <TableCell className="px-1 text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-ink-subtle hover:text-destructive"
                      onClick={() => removeLineItem(i)}
                      disabled={lineItems.length === 1}
                      aria-label={`Remove line item ${i + 1}`}
                    >
                      <Trash2 strokeWidth={1.5} />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </section>

      <section className="mt-10">
        <SectionRule label="Totals" />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Tax %" htmlFor="edit-invoice-tax">
            <Input
              id="edit-invoice-tax"
              type="number"
              value={taxPercent}
              onChange={e => setTaxPercent(e.target.value)}
              onBlur={() => setTaxPercent(prev => prev === '' ? 0 : prev)}
              className="tabular-nums"
              disabled={isPaid}
            />
          </Field>
          <Field label="Due date" htmlFor="edit-invoice-due-date">
            <Input
              id="edit-invoice-due-date"
              type="date"
              value={dueDate}
              onChange={e => setDueDate(e.target.value)}
              className="tabular-nums"
              disabled={isPaid}
            />
          </Field>
        </div>

        <dl className="ml-auto mt-8 max-w-[280px] space-y-2">
          <div className="flex justify-between">
            <dt className="text-small text-ink-muted">Subtotal</dt>
            <dd className="text-small tabular-nums text-ink">{formatCurrency(subtotal)}</dd>
          </div>
          {taxPercent > 0 && (
            <div className="flex justify-between">
              <dt className="text-small text-ink-muted">Tax ({taxPercent}%)</dt>
              <dd className="text-small tabular-nums text-ink">{formatCurrency(taxAmount)}</dd>
            </div>
          )}
          <div className="flex items-baseline justify-between border-t border-line-strong pt-3">
            <dt className="text-micro uppercase text-ink-subtle">Total</dt>
            <dd className="font-sans text-metric tabular-nums text-ink">{formatCurrency(total)}</dd>
          </div>
        </dl>
      </section>

      <section className="mt-10" data-testid="invoice-deliverables">
        <SectionRule
          label="Deliverables"
          action={invoice.project_id ? (
            <>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleUpload}
                disabled={uploading}
                data-testid="invoice-deliverable-file-input"
              />
              <Button
                variant="outline"
                size="sm"
                disabled={uploading}
                onClick={() => fileInputRef.current?.click()}
                data-testid="upload-invoice-deliverable-btn"
              >
                {uploading ? <><Loader2 strokeWidth={1.5} className="animate-spin" /> Uploading…</> : 'Upload file'}
              </Button>
            </>
          ) : undefined}
        />

        {!invoice.project_id ? (
          <p className="text-small text-ink-subtle">
            This invoice isn't linked to a project, so there's nowhere to attach a deliverable.
          </p>
        ) : deliverables.length === 0 ? (
          <EmptyState
            title="No deliverables yet"
            description="Forgot to upload files on the project? Add them here. New files gate on this invoice by default."
          />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>File</TableHead>
                  <TableHead>Access</TableHead>
                  <TableHead className="w-10"><span className="sr-only">Actions</span></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deliverables.map(d => (
                  <TableRow key={d.id} data-testid={`invoice-deliverable-${d.id}`}>
                    <TableCell className="text-ink">{d.file_name}</TableCell>
                    <TableCell>
                      <StatusDot status={d.is_locked ? 'locked' : 'unlocked'} />
                      {d.invoice_id === id && (
                        <span className="ml-2 text-small text-ink-subtle">Gated to this invoice</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {d.is_locked && d.invoice_id === id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleUnassign(d.id)}
                          data-testid={`unassign-deliverable-${d.id}`}
                        >
                          Detach
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Button variant="ghost" size="sm" className="mt-3" asChild>
              <a href={`/projects/${invoice.project_id}`}>Manage all project deliverables</a>
            </Button>
          </>
        )}
      </section>

      {!isPaid && (
        <div className="sticky bottom-0 z-10 mt-10 flex justify-end gap-3 border-t border-line bg-paper/95 py-4">
          <Button variant="ghost" onClick={() => navigate('/invoices')}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      )}
    </div>
  );
}
